import { Capacitor } from '@capacitor/core';
import { SalaNoCelular } from 'sala-no-celular';
import { api } from '@/utils/api';
import { getApiBase } from '@/config';
import { App } from '@capacitor/app';
import { lerVersao } from '@/utils/versao';
import { fraseDaFalhaDoPreparo } from '@/features/aplicativo/falhaDoPreparo';
import { salasAdotaveis, salasAEncerrarNoPortal } from '@/features/aplicativo/salasParaOPortal';
import { encerrarNoPortal } from '@/features/aplicativo/encerrarNoPortal';

/**
 * A SALA NO CELULAR E NO iPAD (app 1.5, 31/10/2026). O motor é a sala do aplicativo do computador rodando no próprio
 * aparelho (plugins/sala-no-celular); esta tela só fala com ele pela porta de controle (`controle`). Ver o alto de
 * `plugins/sala-no-celular/src/index.ts`.
 */
export const VERSAO_DA_SALA = '2.0.19';

/**
 * O BUILD NATIVO EM QUE A SALA NO CELULAR NÃO FECHA O APP (31/10/2026, bancada, rodada 2). O motor vai DENTRO do binário:
 * no iPhone/iPad, o do build 77 enviava os resultados pelo `fetch` do Node, que sem JIT derruba o processo — e o app —
 * meio segundo depois de "Encerrar" e a cada minuto (desktop/src/celular/buscar.js). O 78 traz o motor consertado
 * (29604ac1). Abaixo dele a sala no celular não aparece e o motor NÃO liga: os resultados já gravados ficam no aparelho e
 * saem quando o app for atualizado. No Android o Node tem JIT e o `fetch` funciona: o APK de teste (versionCode 7) segue.
 */
export const BUILD_DA_SALA_NO_CELULAR = { ios: 78, android: 7 } as const;
const minimoDaSala = () => (Capacitor.getPlatform() === 'ios' ? BUILD_DA_SALA_NO_CELULAR.ios : BUILD_DA_SALA_NO_CELULAR.android);

/**
 * O TIPO DE APARELHO e A CHAVE DA SALA (1.5, 31/10/2026): a sala vale no celular, no iPad e no tablet Android; o servidor
 * pode desligá-la SÓ no iPad ou SÓ no tablet (`GET /api/aplicativo/sala-no-aparelho`, escrito por
 * scripts/sala-no-aparelho.sh), sem build nova. Sem resposta do servidor, vale a última lida neste aparelho; sem nenhuma,
 * LIGADA. iPad: o iPadOS se apresenta como "Macintosh" com toque. Tablet Android: o lado menor da tela com 600 dp ou mais
 * (o corte do Android para tablet).
 */
export type TipoDeAparelho = 'celular' | 'ipad' | 'tablet-android';
export function tipoDeAparelho(): TipoDeAparelho {
  const plataforma = Capacitor.getPlatform();
  if (plataforma === 'ios') return /iPad/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) ? 'ipad' : 'celular';
  if (plataforma === 'android') return Math.min(window.screen.width, window.screen.height) >= 600 ? 'tablet-android' : 'celular';
  return 'celular';
}
const CHAVE_LOCAL = 'sala-no-aparelho:chave';
type Chave = { ipad: boolean; tabletAndroid: boolean };
function chaveGuardada(): Chave | null { try { const v = JSON.parse(localStorage.getItem(CHAVE_LOCAL) ?? 'null'); return v && typeof v === 'object' ? v : null; } catch { return null; } }
let ultimaConsulta = 0;
/** Pergunta ao servidor (no máximo a cada 30 min, ou já, com `agora`) e guarda neste aparelho. */
export async function consultarChaveDaSala(agora = false): Promise<void> {
  if (!Capacitor.isNativePlatform() || (!agora && Date.now() - ultimaConsulta < 30 * 60 * 1000)) return;
  ultimaConsulta = Date.now();
  try {
    const r = await fetch(`${getApiBase()}/api/aplicativo/sala-no-aparelho`, { cache: 'no-store' });
    if (r.ok) { const d = await r.json(); localStorage.setItem(CHAVE_LOCAL, JSON.stringify({ ipad: d?.ipad !== false, tabletAndroid: d?.tabletAndroid !== false })); }
  } catch { /* sem internet: fica a última */ }
}
/** A sala está ligada neste TIPO de aparelho? (O celular sempre.) */
export function salaLigadaNesteAparelho(): boolean {
  const t = tipoDeAparelho();
  const c = chaveGuardada();
  if (t === 'ipad') return c?.ipad !== false;
  if (t === 'tablet-android') return c?.tabletAndroid !== false;
  return true;
}
if (Capacitor.isNativePlatform()) void consultarChaveDaSala(true);

/**
 * O aplicativo deste aparelho tem o motor da sala, num build que o aguenta, e a sala está ligada neste tipo de aparelho?
 * No navegador, no app sem o plugin, abaixo de `BUILD_DA_SALA_NO_CELULAR` e com a chave desligada, não. O build vem do cache de `utils/versao.ts` (lido na subida): antes de ele chegar, "não".
 */
export const salaNoCelularDisponivel = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SalaNoCelular') && Number(lerVersao().nativo) >= minimoDaSala() && salaLigadaNesteAparelho();

let motor: Promise<unknown> | null = null;
/** Liga o motor — e RECUSA abaixo de `BUILD_DA_SALA_NO_CELULAR`, conferindo o build na hora (não só pelo cache). */
export function ligarMotor(): Promise<unknown> {
  if (!motor) {
    motor = App.getInfo()
      .then((info) => {
        if (!(Number(info.build) >= minimoDaSala())) throw new Error('A sala neste aparelho pede o aplicativo atualizado. Atualize o Portal Simulados e tente de novo.');
        if (!salaLigadaNesteAparelho()) throw new Error('A sala sem internet está desligada neste tipo de aparelho por enquanto. Use o celular ou o computador.');
        return SalaNoCelular.iniciarMotor();
      })
      .catch((e) => { motor = null; throw e; });
  }
  return motor;
}

export async function controle<T = Record<string, unknown>>(metodo: 'GET' | 'POST', caminho: string, corpo?: unknown): Promise<T> {
  await ligarMotor();
  const r = await SalaNoCelular.controle({ metodo, caminho, corpo });
  if (r.status >= 400) throw new Error((r.corpo as { erro?: string })?.erro || 'A sala recusou o pedido.');
  return r.corpo as T;
}

/**
 * "Aplicar na sala" no celular: o portal prepara a sala do aplicativo (a mesma de `aplicarNaSalaSemInternet`, com a
 * versão da sala e a plataforma no cabeçalho — o portal registra que veio do celular) e o motor a guarda no aparelho.
 */
/**
 * O que ESTE motor sabe (1.6): `/estado` traz `aparelho` e `sabe`. O motor do app 1.5 não traz nada — e a tela esconde o
 * que é novo (uma sala por aparelho, preparar sozinho, apagar, salvar o arquivo), sem precisar subir o nativo mínimo.
 */
export interface EstadoDoMotor { salas: unknown[]; aparelho?: { id: string; aplicaSalas: boolean; decidido?: boolean } | null; sabe?: string[] }
export async function estadoDoMotor(): Promise<EstadoDoMotor> { return controle<EstadoDoMotor>('GET', '/estado'); }

export async function prepararNoCelular(simulationId: number, turmas?: number[], opcoes: { automatica?: boolean } = {}): Promise<{ sala: { id: number; titulo: string; alunos: number }; aparelhoMarcado?: boolean }> {
  // UMA SALA POR APARELHO (1.6): o id deste aparelho e as salas que ele já guarda — só se o motor souber (1.6+).
  const est = await estadoDoMotor().catch(() => null);
  /*
   * O MOTOR NÃO RESPONDEU (03/11/2026, a sala 50 do #1265): antes o preparo seguia sem o id do aparelho e com a versão de
   * reserva, e o portal criava uma sala "sem aparelho" — que a preparação sozinha seguinte não reconhecia, e duplicava.
   * Sem resposta do motor, nada vai ao portal.
   */
  if (!est) throw new Error('A sala deste aparelho não respondeu. Feche e abra o aplicativo e tente de novo.');
  const aparelho = est.aparelho ?? null;
  // Só as salas que o portal pode adotar; e as deste simulado que o aparelho já encerrou e enviou, o portal encerra antes
  // (`salasParaOPortal.ts`) — senão ele as reaproveitava e o motor recusava a sala "já aberta".
  const salasNoAparelho = aparelho ? salasAdotaveis(est.salas as Parameters<typeof salasAdotaveis>[0]) : [];
  if (aparelho) await encerrarNoPortal(salasAEncerrarNoPortal(est.salas as Parameters<typeof salasAdotaveis>[0], simulationId));
  if (opcoes.automatica && !est.sabe?.includes('automatica')) throw new Error('Este aparelho precisa do aplicativo atualizado para preparar sozinho.');
  let dados: Record<string, unknown>;
  try {
    dados = (await api.post('/api/offline-rooms/aplicativo', { simulationId, ...(turmas?.length ? { turmas } : {}), ...(salasNoAparelho.length ? { salasNoAparelho } : {}), ...(opcoes.automatica ? { automatica: true } : {}) }, {
      headers: { 'X-Portal-Simulados-Versao': (est as { versao?: string }).versao ?? VERSAO_DA_SALA, 'X-Portal-Simulados-Plataforma': Capacitor.getPlatform(), ...(aparelho ? { 'X-Portal-Simulados-Aparelho': aparelho.id } : {}) },
    })).data;
  } catch (e) {
    // A causa real (03/11/2026): recusa por regra, sessão, servidor ou rede — "confira a internet" só quando é a rede.
    throw new Error(fraseDaFalhaDoPreparo(e));
  }
  // O endereço do PORTAL (para o envio dos resultados): o da API, e não a origem da página (no app é https://localhost).
  const portal = (getApiBase() || 'https://www.portalsimulados.com.br').replace(/\/$/, '');
  // O nome da rede do preparo (Android; no iPhone/iPad vem nulo): o lembrete do Wi-Fi da sala compara com ele antes de abrir.
  const ssid = await SalaNoCelular.redeAtual().then((r) => r.ssid ?? null).catch(() => null);
  const r = await controle<{ sala: { id: number; titulo: string; alunos: number }; aparelhoMarcado?: boolean }>('POST', '/preparar', { ...dados, portal, ssid, ...(opcoes.automatica ? { automatica: true } : {}) });
  return r;
}
